import { useEffect, useState } from "react";
import { Layout } from "@/components/Layout";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { PlayCircle, Star } from "@phosphor-icons/react";
import { Link } from "react-router-dom";

export default function Training() {
  const { isMember } = useAuth();
  const [videos, setVideos] = useState([]);
  const [activeCat, setActiveCat] = useState("All");
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isMember) { setLoading(false); return; }
    (async () => {
      try {
        const { data } = await api.get("/videos");
        setVideos(data);
      } catch (err) {
        console.error("Failed to load videos:", err);
      }
      setLoading(false);
    })();
  }, [isMember]);

  if (!isMember) {
    return (
      <Layout>
        <div className="max-w-3xl mx-auto px-4 py-24 text-center" data-testid="training-gated">
          <div className="section-label mb-4">— RESTRICTED</div>
          <h1 className="font-chivo font-black text-4xl text-white uppercase mb-4">Training Center</h1>
          <p className="font-plex text-[#9CA3AF] mb-8">Activate your membership to access the full video library.</p>
          <Link to="/billing" className="btn-primary">Activate Membership</Link>
        </div>
      </Layout>
    );
  }

  const categories = ["All", ...Array.from(new Set(videos.map((v) => v.category)))];
  const filtered = activeCat === "All" ? videos : videos.filter((v) => v.category === activeCat);
  const featured = videos.filter((v) => v.featured);

  return (
    <Layout>
      <div className="max-w-[1400px] mx-auto px-4 lg:px-8 py-10">
        <div className="mb-8">
          <div className="section-label mb-3">— TRAINING CENTER</div>
          <h1 className="font-chivo font-black text-4xl lg:text-5xl text-white uppercase tracking-tight" data-testid="training-headline">Video Library</h1>
          <p className="font-plex text-[#9CA3AF] mt-3 max-w-2xl">Step-by-step instruction from the Dry Ice Bulls team. New videos added regularly.</p>
        </div>

        {selected && (
          <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" onClick={() => setSelected(null)} data-testid="video-modal">
            <div className="w-full max-w-5xl" onClick={(e) => e.stopPropagation()}>
              <div className="aspect-video bg-black border border-[#262626]">
                {(() => {
                  if (selected.youtube_url) {
                    return <iframe src={selected.youtube_url} className="w-full h-full" title={selected.title} allowFullScreen allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture" />;
                  }
                  if (selected.storage_path) {
                    return <video src={selected.storage_path} className="w-full h-full" controls />;
                  }
                  return <div className="w-full h-full flex items-center justify-center text-white">No source</div>;
                })()}
              </div>
              <div className="mt-4 flex items-center justify-between">
                <div>
                  <div className="font-chivo font-bold text-2xl text-white">{selected.title}</div>
                  <div className="font-mono text-xs text-[#9CA3AF] mt-1">{selected.category} · {selected.duration}</div>
                </div>
                <button onClick={() => setSelected(null)} className="btn-secondary text-xs" data-testid="close-video-btn">Close</button>
              </div>
              <p className="font-plex text-sm text-[#9CA3AF] mt-3">{selected.description}</p>
            </div>
          </div>
        )}

        {/* Featured */}
        {featured.length > 0 && activeCat === "All" && (
          <div className="mb-10">
            <div className="section-label mb-4">FEATURED</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {featured.slice(0, 3).map((v) => (
                <button key={v.id} onClick={() => setSelected(v)} className="surface surface-hover text-left group" data-testid={`featured-video-${v.id}`}>
                  <div className="aspect-video relative overflow-hidden">
                    {v.thumbnail_url && <img src={v.thumbnail_url} alt={v.title} className="w-full h-full object-cover" />}
                    <div className="absolute inset-0 bg-black/50 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                      <PlayCircle size={56} weight="duotone" className="text-[#00D4FF]" />
                    </div>
                    <div className="absolute top-3 left-3 tag tag-active flex items-center gap-1">
                      <Star size={10} weight="fill" /> FEATURED
                    </div>
                  </div>
                  <div className="p-4">
                    <div className="font-mono text-[10px] text-[#00D4FF] tracking-widest uppercase mb-1">{v.category}</div>
                    <div className="font-chivo font-bold text-white">{v.title}</div>
                    <div className="font-mono text-xs text-[#52525B] mt-1">{v.duration}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Category filter */}
        <div className="flex flex-wrap gap-2 mb-6" data-testid="video-category-filter">
          {categories.map((c) => (
            <button key={c} onClick={() => setActiveCat(c)} className={`tag ${activeCat === c ? "tag-active" : ""}`} data-testid={`category-${c.toLowerCase().replace(/\s+/g, "-")}`}>
              {c}
            </button>
          ))}
        </div>

        {/* Grid */}
        {loading ? (
          <div className="font-mono text-xs text-[#52525B]">[LOADING]</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="videos-grid">
            {filtered.map((v) => (
              <button key={v.id} onClick={() => setSelected(v)} className="surface surface-hover text-left group" data-testid={`video-card-${v.id}`}>
                <div className="aspect-video relative overflow-hidden">
                  {v.thumbnail_url && <img src={v.thumbnail_url} alt={v.title} className="w-full h-full object-cover" />}
                  <div className="absolute inset-0 bg-black/50 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                    <PlayCircle size={48} weight="duotone" className="text-[#00D4FF]" />
                  </div>
                </div>
                <div className="p-4">
                  <div className="font-mono text-[10px] text-[#00D4FF] tracking-widest uppercase mb-1">{v.category}</div>
                  <div className="font-chivo font-bold text-white text-sm">{v.title}</div>
                  <div className="font-mono text-xs text-[#52525B] mt-1">{v.duration}</div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
